/**
 * Time systems and sidereal time.
 *
 * The engine's internal clock is **seconds since the J2000.0 epoch**
 * (2000-01-01 12:00:00 TT). A single scalar keeps the integrator simple and
 * makes runs reproducible; calendar dates only exist at the boundary.
 *
 * Simplification (Tier B): UTC, UT1 and TT are treated as differing only by the
 * constant TT−UTC offset below. Leap seconds and the sub-second UT1−UTC wobble
 * are ignored, which costs at most a few hundred metres of ground-track
 * longitude — far below the fidelity of the atmosphere and gravity models here.
 */

/** Julian date of the J2000.0 epoch. */
export const JD_J2000 = 2451545.0;

/** Julian date of the Unix epoch, 1970-01-01T00:00:00Z. */
export const JD_UNIX_EPOCH = 2440587.5;

export const SECONDS_PER_DAY = 86400;
export const JULIAN_CENTURY_DAYS = 36525;

/**
 * TT − UTC in seconds, as of 2017-01-01 (37 leap seconds + 32.184 s).
 * Constant here by design; see the module note.
 */
export const TT_MINUS_UTC = 69.184;

/** Julian date (UTC) from a JavaScript Date. */
export const dateToJulian = (date: Date): number =>
  date.getTime() / (SECONDS_PER_DAY * 1000) + JD_UNIX_EPOCH;

/** JavaScript Date from a Julian date (UTC). */
export const julianToDate = (jd: number): Date =>
  new Date((jd - JD_UNIX_EPOCH) * SECONDS_PER_DAY * 1000);

/** Seconds since J2000.0 from a JavaScript Date. */
export const dateToEpochSeconds = (date: Date): number =>
  (dateToJulian(date) - JD_J2000) * SECONDS_PER_DAY + TT_MINUS_UTC;

/** JavaScript Date from seconds since J2000.0. */
export const epochSecondsToDate = (t: number): Date =>
  julianToDate(JD_J2000 + (t - TT_MINUS_UTC) / SECONDS_PER_DAY);

/** Julian date from seconds since J2000.0. */
export const epochSecondsToJulian = (t: number): number => JD_J2000 + t / SECONDS_PER_DAY;

/** Julian centuries since J2000.0 from seconds since J2000.0. */
export const julianCenturies = (t: number): number =>
  t / (SECONDS_PER_DAY * JULIAN_CENTURY_DAYS);

/**
 * Greenwich Mean Sidereal Time in radians, from seconds since J2000.0.
 *
 * IAU 1982 expression (Vallado, *Fundamentals of Astrodynamics and
 * Applications*, eq. 3-47). The polynomial yields seconds of time, which convert
 * to degrees at 1/240 deg per second (86400 s ↔ 360 deg).
 *
 * This is the single hinge between the inertial frame the physics runs in and
 * the rotating frame the user sees, so it is worth getting exactly right: a 1 s
 * error here displaces a ground track by ~465 m at the equator.
 */
export function gmst(tEpochSeconds: number): number {
  const T = julianCenturies(tEpochSeconds);
  const seconds =
    67310.54841 +
    (876600 * 3600 + 8640184.812866) * T +
    0.093104 * T * T -
    6.2e-6 * T * T * T;
  let deg = (seconds / 240) % 360;
  if (deg < 0) deg += 360;
  return (deg * Math.PI) / 180;
}

/**
 * Local Mean Sidereal Time in radians for a given east longitude (radians).
 * This is the right-ascension a ground site is pointing at, and therefore the
 * launch azimuth reference.
 */
export function lmst(tEpochSeconds: number, eastLongitude: number): number {
  const t = (gmst(tEpochSeconds) + eastLongitude) % (2 * Math.PI);
  return t < 0 ? t + 2 * Math.PI : t;
}

/**
 * Equation of time in seconds — the offset between apparent solar time and mean
 * solar time. Needed to place the day/night terminator and shadow correctly.
 * Simplified series, accurate to about 10 s.
 */
export function equationOfTime(tEpochSeconds: number): number {
  const d = tEpochSeconds / SECONDS_PER_DAY;
  const B = (2 * Math.PI * (d - 81)) / 365.2422;
  return 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
}

/** ISO-8601 string for a mission epoch, for export files and report headers. */
export const epochSecondsToIso = (t: number): string => epochSecondsToDate(t).toISOString();

/** Parse an ISO-8601 date string to seconds since J2000.0. Throws on bad input. */
export function isoToEpochSeconds(iso: string): number {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) throw new Error(`Invalid ISO-8601 date: ${iso}`);
  return dateToEpochSeconds(date);
}
